<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2>Bonificaciones</h2>
        <p>Creá descuentos adicionales sobre el precio ya calculado por la lista.</p>
      </div>
      <v-btn color="primary" :loading="loading" @click="loadAll">Recargar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense outlined class="mb-4">{{ success }}</v-alert>
    <v-alert v-if="info" type="info" dense outlined class="mb-4">{{ info }}</v-alert>
    <v-alert v-if="rows.length" type="info" dense outlined class="mb-4">
      Semáforo de conflictos:
      <strong class="ml-1">Verde {{ conflictSummary.green }}</strong>,
      <strong class="ml-1">Amarillo {{ conflictSummary.yellow }}</strong>,
      <strong class="ml-1">Rojo {{ conflictSummary.red }}</strong>.
    </v-alert>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">Filtrar descuentos ya creados <span class="caption ml-2">(opcional; sólo ordena el listado de abajo)</span></v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.priceListId"
              :items="priceListOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Lista de precios"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.rubroCode" :items="rubroOptions" item-text="label" item-value="value" clearable label="Familia / rubro" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="filters.productType" :items="productTypeOptions" item-text="text" item-value="value" clearable label="Tipo producto" outlined dense />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.providerId"
              :items="providerOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Proveedor"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.bonusSearch"
              clearable
              label="Buscar artículo o proveedor"
              prepend-inner-icon="mdi-magnify"
              hint="Buscá por código o nombre. El listado se actualiza mientras escribís."
              persistent-hint
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.customerType"
              :items="customerTypeOptions"
              item-text="text"
              item-value="value"
              clearable
              label="Tipo cliente"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.isActive"
              :items="activeOptions"
              item-text="text"
              item-value="value"
              label="Estado"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-end">
            <v-btn color="primary" block :loading="loading" @click="fetchBonuses">Filtrar listado</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined class="page-card mb-4">
      <v-card-title class="subtitle-1">{{ form.id ? "Editar descuento adicional" : "Crear descuento adicional" }}</v-card-title>
      <v-card-text>
        <v-alert type="info" dense outlined class="mb-3">Elegí un alcance principal: artículo, familia/rubro, tipo de producto o proveedor. Dejá vacíos los demás si no querés combinarlos.</v-alert>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="form.productCode"
              :search-input.sync="form.productSearch"
              :items="formProductOptions"
              :loading="suggestLoading"
              item-text="label"
              item-value="code"
              clearable
              hide-no-data
              label="Artículo"
              outlined
              dense
              @input="onProductSelection"
              @update:search-input="onProductSearchForm"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="form.priceListId"
              :items="priceListOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Lista de precios"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="form.rubroCode" :items="rubroOptions" item-text="label" item-value="value" clearable label="Familia / rubro" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-select v-model="form.productType" :items="productTypeOptions" item-text="text" item-value="value" clearable label="Tipo producto" outlined dense />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="form.providerId"
              :items="providerOptions"
              item-text="label"
              item-value="id"
              clearable
              label="Proveedor"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="form.customerType"
              :items="customerTypeOptions"
              item-text="text"
              item-value="value"
              label="Tipo cliente"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="form.minQuantity" type="number" min="1" step="1" label="Cantidad mínima" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="form.maxQuantity" type="number" min="1" step="1" label="Cantidad máxima (opc.)" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="form.bonusType"
              :items="bonusTypeOptions"
              item-text="text"
              item-value="value"
              label="Tipo bonificación"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="form.bonusValue"
              type="number"
              min="0"
              step="0.01"
              :label="form.bonusType === 'percent' ? 'Valor (%)' : 'Monto'"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model.number="form.priority" type="number" step="1" label="Prioridad" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="form.validFrom" type="date" label="Vigencia desde" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-text-field v-model="form.validTo" type="date" label="Vigencia hasta" outlined dense />
          </v-col>
          <v-col cols="12" md="8">
            <v-text-field v-model="form.description" label="Descripción" outlined dense />
          </v-col>
          <v-col cols="12" md="2">
            <v-switch v-model="form.isActive" inset label="Activa" />
          </v-col>
        </v-row>
        <div class="actions-row">
          <v-btn color="success" :loading="saving" @click="saveBonus">{{ form.id ? "Actualizar" : "Crear" }}</v-btn>
          <v-btn text color="primary" @click="resetForm">Limpiar</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card outlined class="page-card">
      <v-card-title class="subtitle-1">
        Bonificaciones cargadas
        <span class="caption ml-2">{{ visibleRows.length }} de {{ rows.length }}</span>
      </v-card-title>
      <v-data-table :headers="headers" :items="visibleRows" :loading="loading" item-key="id" no-data-text="No encontramos bonificaciones con esa búsqueda." dense>
        <template v-slot:item.risk="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                :color="riskColor(item)"
                text-color="white"
                v-bind="attrs"
                v-on="on"
              >
                {{ riskLabel(item) }}
              </v-chip>
            </template>
            <span>{{ riskReason(item) }}</span>
          </v-tooltip>
        </template>
        <template v-slot:item.articleDisplay="{ item }">
          <div v-if="item.product" class="commercial-cell">
            <strong>{{ item.product.code }}</strong>
            <span>{{ item.product.name || "Sin nombre comercial" }}</span>
          </div>
          <span v-else class="muted-cell">Alcance general</span>
        </template>
        <template v-slot:item.providerDisplay="{ item }">
          <div v-if="item.provider" class="commercial-cell">
            <strong>{{ item.provider.name || "Proveedor sin nombre" }}</strong>
            <span v-if="item.provider.code">Código {{ item.provider.code }}</span>
          </div>
          <span v-else class="muted-cell">Todos los proveedores</span>
        </template>
        <template v-slot:item.customerType="{ item }">{{ customerTypeLabel(item.customerType) }}</template>
        <template v-slot:item.bonusType="{ item }">{{ item.bonusType === "percent" ? "Porcentaje" : "Monto" }}</template>
        <template v-slot:item.bonusValue="{ item }">{{ formatBonusValue(item) }}</template>
        <template v-slot:item.range="{ item }">{{ rangeLabel(item) }}</template>
        <template v-slot:item.validity="{ item }">{{ validityLabel(item) }}</template>
        <template v-slot:item.isActive="{ item }"><span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">{{ item.isActive ? "Activa" : "Inactiva" }}</span></template>
        <template v-slot:item.actions="{ item }">
          <v-btn text small color="primary" @click="editRow(item)">Editar</v-btn>
          <v-btn text small :color="item.isActive ? 'error' : 'success'" @click="toggleActive(item)">{{ item.isActive ? "Desactivar" : "Activar" }}</v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { pricingStockService } from "../services/pricingStock";
import { priceListsService } from "../services/priceLists";

function nullableText(v) {
  const s = String(v || "").trim();
  return s || null;
}

export default {
  name: "PricingBonuses",
  data() {
    return {
      loading: false,
      saving: false,
      suggestLoading: false,
      error: "",
      success: "",
      info: "",
      rows: [],
      providers: [],
      priceLists: [],
      rubros: [],
      formProductSuggestions: [],
      selectedFormProduct: null,
      formSuggestionRequest: 0,
      filters: {
        productCode: "",
        productSearch: "",
        bonusSearch: "",
        priceListId: "",
        providerId: "",
        rubroCode: "",
        productType: "",
        customerType: "",
        isActive: "all",
      },
      form: {
        id: "",
        productCode: "",
        productSearch: "",
        priceListId: "",
        providerId: "",
        rubroCode: "",
        productType: "",
        customerType: "all",
        minQuantity: 1,
        maxQuantity: null,
        bonusType: "percent",
        bonusValue: 0,
        priority: 0,
        validFrom: "",
        validTo: "",
        description: "",
        isActive: true,
      },
      headers: [
        { text: "Semáforo", value: "risk", sortable: false },
        { text: "Artículo", value: "articleDisplay" },
        { text: "Lista", value: "priceList.code" },
        { text: "Proveedor", value: "providerDisplay" },
        { text: "Familia", value: "rubroCode" },
        { text: "Tipo producto", value: "productType" },
        { text: "Tipo cliente", value: "customerType" },
        { text: "Rango cant.", value: "range", sortable: false },
        { text: "Tipo", value: "bonusType" },
        { text: "Valor", value: "bonusValue" },
        { text: "Vigencia", value: "validity", sortable: false },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      customerTypeOptions: [
        { text: "Todos", value: "all" },
        { text: "Consumidor final", value: "final_consumer" },
        { text: "Cuenta corriente", value: "account_current" },
      ],
      bonusTypeOptions: [
        { text: "Porcentaje", value: "percent" },
        { text: "Monto", value: "amount" },
      ],
      productTypeOptions: [
        { text: "Almacenable", value: "storable" },
        { text: "Consumible", value: "consumable" },
        { text: "Servicio", value: "service" },
      ],
      activeOptions: [
        { text: "Todos", value: "all" },
        { text: "Activas", value: true },
        { text: "Inactivas", value: false },
      ],
    };
  },
  computed: {
    providerOptions() {
      return this.providers.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    priceListOptions() {
      return this.priceLists.map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    rubroOptions() {
      return this.rubros.map((x) => ({ value: x.rubro, label: `${x.rubro}${x.name ? ` - ${x.name}` : ""}` }));
    },
    formProductOptions() {
      const selected = this.selectedFormProduct;
      if (!selected) return this.formProductSuggestions;
      if (this.formProductSuggestions.some((item) => item.code === selected.code)) return this.formProductSuggestions;
      return [selected, ...this.formProductSuggestions];
    },
    visibleRows() {
      const search = this.normalizeSearch(this.filters.bonusSearch);
      if (!search) return this.rows;
      return this.rows.filter((item) => {
        const product = item.product || {};
        const provider = item.provider || {};
        const text = [
          product.code,
          product.name,
          provider.code,
          provider.name,
          item.description,
          item.rubroCode,
        ].map(this.normalizeSearch).join(" ");
        return text.includes(search);
      });
    },
    conflictById() {
      const map = new Map();
      const activeRows = this.rows.filter((x) => Boolean(x.isActive));
      for (let i = 0; i < activeRows.length; i += 1) {
        const a = activeRows[i];
        for (let j = i + 1; j < activeRows.length; j += 1) {
          const b = activeRows[j];
          if (!this.sameListAndProduct(a, b)) continue;
          if (!this.overlapsQuantity(a, b)) continue;
          if (!this.overlapsValidity(a, b)) continue;

          const sameCustomer = (a.customerType || "all") === (b.customerType || "all");
          const sameProvider = String(a.providerId || "") === String(b.providerId || "");
          const level = sameCustomer && sameProvider ? "red" : "yellow";

          this.addConflict(map, a.id, level, `Se superpone con ${b.id}`);
          this.addConflict(map, b.id, level, `Se superpone con ${a.id}`);
        }
      }
      return map;
    },
    conflictSummary() {
      let green = 0;
      let yellow = 0;
      let red = 0;
      for (let i = 0; i < this.rows.length; i += 1) {
        const row = this.rows[i];
        const risk = this.conflictById.get(row.id);
        if (!risk) green += 1;
        else if (risk.level === "red") red += 1;
        else yellow += 1;
      }
      return { green, yellow, red };
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    normalizeSearch(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },
    addConflict(map, id, level, reason) {
      const current = map.get(id);
      if (!current) {
        map.set(id, { level, reasons: [reason] });
        return;
      }
      if (current.level !== "red" && level === "red") current.level = "red";
      current.reasons.push(reason);
      map.set(id, current);
    },
    sameListAndProduct(a, b) {
      const aList = String(a.priceListId || "");
      const bList = String(b.priceListId || "");
      const aProduct = String((a.product && a.product.id) || a.productId || "");
      const bProduct = String((b.product && b.product.id) || b.productId || "");
      if (!aList || !bList || !aProduct || !bProduct) return false;
      return aList === bList && aProduct === bProduct;
    },
    overlapsQuantity(a, b) {
      const aMin = Number(a.minQuantity || 1);
      const aMax = a.maxQuantity === null || a.maxQuantity === undefined ? Number.MAX_SAFE_INTEGER : Number(a.maxQuantity);
      const bMin = Number(b.minQuantity || 1);
      const bMax = b.maxQuantity === null || b.maxQuantity === undefined ? Number.MAX_SAFE_INTEGER : Number(b.maxQuantity);
      return aMin <= bMax && bMin <= aMax;
    },
    overlapsValidity(a, b) {
      const aFrom = a.validFrom || "0000-01-01";
      const aTo = a.validTo || "9999-12-31";
      const bFrom = b.validFrom || "0000-01-01";
      const bTo = b.validTo || "9999-12-31";
      return aFrom <= bTo && bFrom <= aTo;
    },
    riskLabel(item) {
      const risk = this.conflictById.get(item.id);
      if (!risk) return "Verde";
      return risk.level === "red" ? "Rojo" : "Amarillo";
    },
    riskColor(item) {
      const risk = this.conflictById.get(item.id);
      if (!risk) return "green";
      return risk.level === "red" ? "red" : "orange";
    },
    riskReason(item) {
      const risk = this.conflictById.get(item.id);
      if (!risk) return "Sin superposición detectada.";
      if (risk.level === "red") {
        return "Conflicto alto: misma lista, mismo artículo, mismo rango/cantidad y misma condición cliente/proveedor.";
      }
      return "Atención: superposición parcial en lista/artículo/cantidad; revisar condiciones de cliente o proveedor.";
    },
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    getError(error) {
      if (error && error.response && error.response.data) {
        if (error.response.data.message) return error.response.data.message;
        if (error.response.data.errors) return String(error.response.data.errors);
      }
      if (error && error.message) return error.message;
      return "No pudimos completar la operación.";
    },
    customerTypeLabel(value) {
      if (value === "final_consumer") return "Consumidor final";
      if (value === "account_current") return "Cuenta corriente";
      return "Todos";
    },
    rangeLabel(item) {
      const min = Number(item.minQuantity || 1);
      const max = item.maxQuantity === null || item.maxQuantity === undefined ? null : Number(item.maxQuantity);
      return max ? `${min} a ${max}` : `Desde ${min}`;
    },
    validityLabel(item) {
      const from = item.validFrom || "—";
      const to = item.validTo || "—";
      return `${from} / ${to}`;
    },
    formatBonusValue(item) {
      const n = Number(item.bonusValue || 0);
      if (item.bonusType === "percent") return `${n}%`;
      return n;
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      this.success = "";
      try {
        const [providersRes, listsRes, rubrosRes] = await Promise.all([
          pricingStockService.getProviders({ isActive: true }),
          priceListsService.list(),
          pricingStockService.getRubrosCatalog({}),
        ]);
        this.providers = this.unwrap(providersRes) || [];
        this.priceLists = this.unwrap(listsRes) || [];
        this.rubros = this.unwrap(rubrosRes) || [];
        await this.fetchBonuses();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    async onProductSearchForm(q) {
      this.form.productSearch = q || "";
      await this.fetchFormProductSuggestions(q || "");
    },
    onProductSelection(code) {
      if (!code) {
        this.selectedFormProduct = null;
        return;
      }
      const selected = this.formProductSuggestions.find((item) => item.code === code)
        || (this.selectedFormProduct && this.selectedFormProduct.code === code ? this.selectedFormProduct : null);
      if (selected) this.selectedFormProduct = selected;
    },
    async fetchFormProductSuggestions(q) {
      const request = ++this.formSuggestionRequest;
      if (!q || q.length < 2) {
        const selected = this.formProductSuggestions.find((item) => item.code === this.form.productCode);
        this.formProductSuggestions = selected ? [selected] : [];
        return;
      }
      this.suggestLoading = true;
      try {
        const response = await pricingStockService.getSalesCatalogSuggest({ q, priceListId: this.form.priceListId || this.filters.priceListId || undefined });
        const rows = this.unwrap(response) || [];
        if (request !== this.formSuggestionRequest) return;
        this.formProductSuggestions = rows.map((x) => ({ ...x, label: `${x.code} - ${x.name}` }));
      } catch (_error) {
        if (request !== this.formSuggestionRequest) return;
        this.formProductSuggestions = [];
      } finally {
        this.suggestLoading = false;
      }
    },
    async fetchBonuses() {
      this.loading = true;
      this.error = "";
      this.info = "";
      try {
        const params = {};
        if (this.filters.productCode) params.productCode = this.filters.productCode;
        if (this.filters.priceListId) params.priceListId = this.filters.priceListId;
        if (this.filters.providerId) params.providerId = this.filters.providerId;
        if (this.filters.rubroCode) params.rubroCode = this.filters.rubroCode;
        if (this.filters.productType) params.productType = this.filters.productType;
        if (this.filters.customerType) params.customerType = this.filters.customerType;
        if (this.filters.isActive !== "all") params.isActive = this.filters.isActive;
        const response = await pricingStockService.getBonuses(params);
        this.rows = this.unwrap(response) || [];
        if (!this.rows.length) this.info = "No hay bonificaciones para los filtros seleccionados.";
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.loading = false;
      }
    },
    buildPayloadFromForm() {
      const minQ = Number(this.form.minQuantity || 0);
      const maxQ = this.form.maxQuantity === null || this.form.maxQuantity === "" ? null : Number(this.form.maxQuantity);
      const value = Number(this.form.bonusValue);

      if (!(minQ > 0)) throw new Error("La cantidad mínima debe ser mayor a 0.");
      if (maxQ !== null && maxQ < minQ) throw new Error("La cantidad máxima no puede ser menor a la mínima.");
      if (!Number.isFinite(value) || value < 0) throw new Error("El valor de bonificación es inválido.");
      if (this.form.productSearch && !this.form.productCode) throw new Error("Elegí el artículo desde los resultados antes de crear el descuento.");
      if (!this.form.productCode && !this.form.providerId && !this.form.rubroCode && !this.form.productType) {
        throw new Error("Elegí un artículo, proveedor, familia/rubro o tipo de producto. No se permite aplicar un descuento adicional a toda la lista.");
      }

      return {
        id: nullableText(this.form.id),
        productCode: nullableText(this.form.productCode),
        priceListId: nullableText(this.form.priceListId),
        providerId: nullableText(this.form.providerId),
        rubroCode: nullableText(this.form.rubroCode),
        productType: nullableText(this.form.productType),
        customerType: this.form.customerType || "all",
        minQuantity: minQ,
        maxQuantity: maxQ,
        bonusType: this.form.bonusType,
        bonusValue: value,
        priority: Number(this.form.priority || 0),
        validFrom: nullableText(this.form.validFrom),
        validTo: nullableText(this.form.validTo),
        description: nullableText(this.form.description),
        isActive: Boolean(this.form.isActive),
      };
    },
    rowToPayload(item, overrides = {}) {
      return {
        id: item.id,
        productCode: item.product ? item.product.code : null,
        priceListId: item.priceListId || null,
        providerId: item.providerId || null,
        rubroCode: item.rubroCode || null,
        productType: item.productType || null,
        customerType: item.customerType || "all",
        minQuantity: Number(item.minQuantity || 1),
        maxQuantity: item.maxQuantity === null || item.maxQuantity === undefined ? null : Number(item.maxQuantity),
        bonusType: item.bonusType || "percent",
        bonusValue: Number(item.bonusValue || 0),
        priority: Number(item.priority || 0),
        validFrom: item.validFrom || null,
        validTo: item.validTo || null,
        description: item.description || null,
        isActive: Boolean(item.isActive),
        ...overrides,
      };
    },
    async saveBonus() {
      this.saving = true;
      this.error = "";
      this.success = "";
      this.info = "";
      try {
        const row = this.buildPayloadFromForm();
        await pricingStockService.upsertBonuses({ items: [row] });
        this.success = this.form.id ? "Bonificación actualizada." : "Bonificación creada.";
        this.resetForm();
        await this.fetchBonuses();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.saving = false;
      }
    },
    editRow(item) {
      this.error = "";
      this.success = "";
      this.info = "";
      this.form = {
        id: item.id,
        productCode: item.product ? item.product.code : "",
        productSearch: item.product ? `${item.product.code} - ${item.product.name || ""}` : "",
        priceListId: item.priceListId || "",
        providerId: item.providerId || "",
        rubroCode: item.rubroCode || "",
        productType: item.productType || "",
        customerType: item.customerType || "all",
        minQuantity: Number(item.minQuantity || 1),
        maxQuantity: item.maxQuantity === null || item.maxQuantity === undefined ? null : Number(item.maxQuantity),
        bonusType: item.bonusType || "percent",
        bonusValue: Number(item.bonusValue || 0),
        priority: Number(item.priority || 0),
        validFrom: item.validFrom || "",
        validTo: item.validTo || "",
        description: item.description || "",
        isActive: Boolean(item.isActive),
      };
      this.selectedFormProduct = item.product ? { code: item.product.code, name: item.product.name || "", label: `${item.product.code} - ${item.product.name || ""}` } : null;
    },
    async toggleActive(item) {
      this.saving = true;
      this.error = "";
      this.success = "";
      try {
        const payload = this.rowToPayload(item, { isActive: !item.isActive });
        await pricingStockService.upsertBonuses({ items: [payload] });
        this.success = payload.isActive ? "Bonificación activada." : "Bonificación desactivada.";
        await this.fetchBonuses();
      } catch (error) {
        this.error = this.getError(error);
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.selectedFormProduct = null;
      this.form = {
        id: "",
        productCode: "",
        productSearch: "",
        priceListId: "",
        providerId: "",
        rubroCode: "",
        productType: "",
        customerType: "all",
        minQuantity: 1,
        maxQuantity: null,
        bonusType: "percent",
        bonusValue: 0,
        priority: 0,
        validFrom: "",
        validTo: "",
        description: "",
        isActive: true,
      };
    },
  },
};
</script>

<style scoped>
.page {
  padding: 0;
}

.page-hero {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.page-hero h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.page-hero p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.page-card {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 8px 8px 4px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.actions-row {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.pill {
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
}

.pill--success {
  color: #065f46;
  background: #d1fae5;
}

.pill--warn {
  color: #991b1b;
  background: #fee2e2;
}

.commercial-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 180px;
  padding: 5px 0;
}

.commercial-cell strong {
  color: var(--text-primary);
  font-size: 12px;
}

.commercial-cell span,
.muted-cell {
  color: var(--text-secondary);
  font-size: 12px;
}

@media (max-width: 960px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
